import Template from "../app/Template";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import Button from "../layout/Button";
import Card from "../layout/Card";
import CardWithImageLeft from "../layout/CardWithImageLeft";
import { MagnifyingGlassIcon, TagIcon } from "@heroicons/react/24/outline";
import PricingCard from "../pricing/PricingCard";
import HorizontalStats from "../stats/HorizontalStats";
import SupportUs from "../landing/SupportUs";
import SectionHeader from "../layout/SectionHeader";
import CardWithImageBgAndBadgeTop from "../layout/CardWithImageBgAndBadgeTop";
import TextInputSubmitOnEnterLeadingIcon from "../forms/TextInputSubmitOnEnterLeadingIcon";
import { getCategoryTopics } from "../../slices/category";
import { Topic } from "../../types/category";
import Spinner from "../layout/Spinner";
import { getAllContentWithTag, getCollections, getFeaturedContent, getMedia, getNewContent, getPosts, getTags } from "../../slices/content";
import { Media, Post } from "../../types/content";
import PostCard from "../layout/PostCard";
import { useSearchParams } from "react-router-dom";
import { withFilters } from "../../utils/searchParams";
import RegisterModal from "../landing/RegisterModal";

const features = [
  {
    title: "Topics",
    text: "Explore ADHD, autism, dyslexia, PDA and more - divided into easy-to-browse themes like diagnosis, parenting, school, and work.",
    href: "/topics-and-collections",
    image: "/img/graphics/images/Topics2.jpg"
  },
  {
    title: "Events",
    text: "Discover upcoming webinars, talks, support groups and social meet-ups. Filter by location, audience, or interest to find what’s right for you or your family.",
    href: "/events",
    image: "/img/graphics/images/Events2.jpg"
  },
  {
    title: "Curated Collections",
    text: "Follow a guided path through complex topics - hand-picked content journeys designed for different stages, questions and lived experiences.",
    href: "/topics-and-collections",
    image: "/img/graphics/images/Collections2.jpg"
  },
  {
    title: "Directory",
    text: "Find trusted services and professionals - mentoring, assessments, tutors and more. All reviewed with inclusivity and/or neuro-affirming practice at the heart.",
    href: "/directory",
    image: "/img/graphics/images/Directory2.jpg"
  },

]

export function getConsumptionColour(consumption: string) {
  switch (consumption) {
    case "Read":
      return "dark-blue";
    case "Watch":
      return "mint-green";
    case "Listen":
      return "orange";
    default:
      return "blue";
  }
}

export function getConsumptionTextColour(consumption: string) {
  switch (consumption) {
    case "Read":
      return "white";
    case "Watch":
      return "dark-blue";
    case "Listen":
      return "white";
    default:
      return "white";
  }
}

export default function Home() {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams()

  // Get app level state from redux store
  const { user, loading: authLoading } = useAppSelector((state) => state.auth);

  // Component level state
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeContentTag, setActiveContentTag] = useState<string>("Featured");
  const [showRegisterModal, setShowRegisterModal] = useState<boolean>(false);

  // On page load, and again once auth resolves (signing in changes what the
  // content endpoints return — ratings in particular).
  //
  // Refetches whatever is currently selected rather than always Featured. It used
  // to hard-code getFeaturedContent, so signing in while "New" was selected fired
  // a featured request that landed after the new-content one and replaced it —
  // and since nothing is flagged featured, it replaced it with an empty list. The
  // button stayed on "New" above no results until it was clicked again.
  useEffect(() => {
    if (authLoading) return;
    dispatch(getCategoryTopics({ pageNumber: 1, query: null }));
    dispatch(getCollections({ pageNumber: 1, query: null }));
    if (activeContentTag === "New") {
      dispatch(getNewContent({ pageNumber: 1, query: null }));
    } else if (activeContentTag !== "Featured") {
      dispatch(getAllContentWithTag({ pageNumber: 1, tag: null, topic: activeContentTag }));
    } else {
      dispatch(getFeaturedContent({ pageNumber: 1, query: null }));
    }
  }, [authLoading]);

  const {
    topics: { data: topicsData, loading: topicsLoading },
  } = useAppSelector((state) => state.category);
  const {
    collections: { data: collectionData, loading: collectionLoading },
    posts: { data: postsData, loading: postsLoading },
  } = useAppSelector((state) => state.content);

  // Functions
  /** Is the active filter a real topic? Gates the "Show more" -> /topic/<slug> link. */
  function checkTopicDataForActiveTopic(topicSlug: string) {
    return topicsData.some((topic: Topic) => topic.slug === topicSlug);
  }

  /**
   * Reflect the selected landing filter in the URL.
   *
   * The three filters are mutually exclusive, so this always clears the other two.
   * Merging into the existing params (as each handler used to) left the previous
   * selection behind — picking a topic and then "Featured" produced
   * `?topic=adhd&tag=Featured`, which describes two filters that cannot both apply.
   */
  function setLandingFilterParams(params: { tag?: string; topic?: string }) {
    setSearchParams(withFilters(searchParams, {
      tag: params.tag ?? null,
      topic: params.topic ?? null,
    }));
  }

  // Constant variables
  const allFilters: Array<{ id: string; type: string; name: string, image: string | null, slug: string }> = [];
  topicsData.forEach((topic: Topic) => allFilters.push({
    id: topic.id,
    type: "topic",
    name: topic.name,
    image: topic.image,
    slug: topic.slug
  }));
  const exploreTopicsAndCollections: Array<{ id: string; type: "Topic" | "Collection"; name: string; image: string | null; slug: null | string }> = [];
  topicsData.forEach((topic: Topic) => exploreTopicsAndCollections.push({
    id: topic.id,
    type: "Topic",
    name: topic.name,
    image: topic.image,
    slug: topic.slug
  }));
  collectionData.forEach((collection: any) => exploreTopicsAndCollections.push({
    id: collection.id,
    type: "Collection",
    name: collection.name,
    image: collection.images.length > 0 ? collection.images[0] : null,
    slug: collection.slug
  }));
  exploreTopicsAndCollections.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Template>

      <RegisterModal show={showRegisterModal} setShow={setShowRegisterModal} />

      {/* Hero */}
      <div
        className="h-[400px] md:h-[600px] lg:h-[800px] flex items-center px-4 md:px-8 lg:px-12"
        style={{
          backgroundImage: 'url("/img/graphics/hero/home-hero.JPG")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          position: 'relative', // Ensures child elements with absolute positioning are positioned relative to this container
        }}
      >
        <Card className="mt-12 md:mt-0 w-2/3 sm:w-1/2 grid grid-cols-1 gap-y-4 relative overflow-visible" paddingX="px-6 sm:px-10 md:px-14" paddingY="py-6 sm:py-10 sm:py-14">
          {/* Header */}
          <h1 className="text-dark-grey text-3xl md:text-5xl lg:text-6xl font-bold">
            Understanding neurodiversity.
          </h1>

          {/* Text */}
          <p className="hidden sm:block text-dark-grey text-sm sm:text-sm md:text-lg lg:text-xl font-medium">
            A neuro-affirming space for anyone navigating ADHD, autism, dyslexia, sensory differences or trying to make sense of what you’re experiencing. We bring together trusted content, practical guidance, and community support, all in one place.
          </p>

          {/* CTA */}
          <Button
            onClick={() => setShowRegisterModal(true)}
            width="fit"
            shadow="md"
          >
            <span>Join now for free</span>
          </Button>

          {/* Bubble image */}
          <img
            src="/img/graphics/bubbles/bubble-top-blue.svg"
            alt="Bubble top blue"
            className="hidden xs:block absolute -bottom-4 -right-20 md:-right-24 w-40 md:w-60 z-10"
          />
        </Card>

      </div>

      {/* What are you interested in today */}
      <div className="grid grid-cols-1 gap-y-12 bg-yellow py-8 md:py-12 lg:py-16 text-center items-center justify-center relative">

        {/* Bubble image */}
        <img
          src="/img/graphics/bubbles/bubble-right-blue.svg"
          alt="Bubble"
          className="absolute -top-16 sm:-top-8 md:-top-20 left-0 w-40 md:w-60 lg:w-80 z-10"
        />

        {/* Header */}
        <SectionHeader
          text="What are you interested in today?"
        />

        {/* Search our directory */}
        <div className="mx-auto">
          <TextInputSubmitOnEnterLeadingIcon
            id="search-directory"
            placeholder="Search our library"
            icon={
              <MagnifyingGlassIcon
                aria-hidden="true"
                className={`pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-dark-grey sm:size-4`}
              />
            }
            widthClass="w-96"
            onChange={(e) => { setSearchQuery(e.target.value) }}
            onClick={() => {
              if (searchQuery.length === 0) return;
              window.location.href = `/search?query=${searchQuery}`;
            }}
            loading={false}
            shadow="md"
          />
        </div>

        {/* Tag filter buttons */}
        <div className="flex flex-wrap gap-2 justify-center items-center mx-10 md:mx-20">
          <Button
            key={`filter-featured`}
            onClick={() => {
              setActiveContentTag("Featured")
              dispatch(getFeaturedContent({ pageNumber: 1, query: null }));
              setLandingFilterParams({ tag: "Featured" })
            }}
            width="fit"
            bgColour={activeContentTag !== "Featured" ? `transparent hover:bg-dark-grey` : `dark-grey`}
            borderColour="dark-grey"
            textColour={activeContentTag !== "Featured" ? `dark-grey hover:text-white` : `white`}
          >
            Featured
          </Button>
          <Button
            key={`filter-new`}
            onClick={() => {
              setActiveContentTag("New")
              dispatch(getNewContent({ pageNumber: 1, query: null }));
              setLandingFilterParams({ tag: "New" })
            }}
            width="fit"
            bgColour={activeContentTag !== "New" ? `transparent hover:bg-dark-grey` : `dark-grey`}
            borderColour="dark-grey"
            textColour={activeContentTag !== "New" ? `dark-grey hover:text-white` : `white`}
          >
            New
          </Button>
          {/* Main topics.
              This row used to render every Tag with show_as_filter=True, which mixed
              main topics in with narrower tags (AI, Coaching, Impulsivity…). It was
              always meant to be topics: the buttons key off `slug` and "Show more"
              navigates to /topic/<slug>, but Tag has no slug field — the API was
              synthesising one from the name, so that link could not reliably resolve.
              Driving the row from Topic gives real slugs and matches the Topics page. */}
          {topicsLoading ? <Spinner size={8} /> : topicsData.map((topic: Topic) => {
            return (
              <Button
                key={`filter-topic-${topic.id}`}
                onClick={() => {
                  setActiveContentTag(topic.slug)
                  dispatch(getAllContentWithTag({
                    pageNumber: 1,
                    tag: null,
                    topic: topic.slug,
                  }));
                  setLandingFilterParams({ topic: topic.slug })
                }}
                width="fit"
                bgColour={activeContentTag !== topic.slug ? `transparent hover:bg-dark-grey` : `dark-grey`}
                borderColour="dark-grey"
                textColour={activeContentTag !== topic.slug ? `dark-grey hover:text-white` : `white`}
              >
                {topic.name}
              </Button>
            );
          })}
        </div>

        {/* Featured content */}
        {postsLoading ? <div className="flex w-full justify-center"><Spinner size={8} /></div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-16 md:px-20 lg:px-32 justify-center">
            {postsData.map((content: Post | Media, index) => {
              return (
                <PostCard
                  key={`content-${index}`}
                  objectId={content.id}
                  // These come from the featured/new/by-topic list endpoints, which
                  // merge content_type onto each item. Resolved explicitly so a
                  // missing value degrades to "Post" rather than reaching the
                  // favourite button as undefined, where it matches no branch.
                  objectType={content.content_type === "Media" ? "Media" : "Post"}
                  image={content.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : content.images[0]}
                  bgColour="off-white"
                  href={content.url}
                  title={content.display_title}
                  authors={content.authors}
                  primaryTags={content.display_primary_tags}
                  synopsis={content.synopsis}
                  consumption={content.consumption}
                  user={user}
                  rating={content.rating}
                  reviews={content.reviews}
                  price={content.price}
                  locked={content.locked}
                />
              )
            })}
          </div>
        )}

        {!["Featured", "New"].includes(activeContentTag) && (
          checkTopicDataForActiveTopic(activeContentTag) && (
            <div className="flex justify-center mt-4">
              <Button
                width="fit"
                bgColour="dark-grey"
                borderColour="dark-grey"
                textColour="white"
                onClick={() => {
                  window.location.href = `/topic/${activeContentTag}`;
                }}
              >
                Show more
              </Button>
            </div>
          )
        )}

      </div>

      {/* What we offer */}
      <div className="grid grid-cols-1 gap-y-6 bg-off-white mb-28 py-8 md:py-12 lg:py-16 text-center items-center justify-center relative">

        {/* Header */}
        <SectionHeader
          text="What we offer"
          subtext="We bring together voices, resources and events that centre lived experience and neuro-affirming professionals. All organised in an easy-to-explore way - so that you can find the information you need or the support and community you want."
        />

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mx-auto px-16 md:px-20 lg:px-48">
          {features.map((feature, index) => {
            return (
              <CardWithImageLeft
                key={`feature-${index}`}
                image={feature.image}
                // imageClass="p-4"
                rightChildren={
                  <>
                    <h1 className="text-xl sm:text-2xl font-bold">{feature.title}</h1>
                    <p className="text-sm sm:text-base">
                      {feature.text}
                    </p>
                    <a href={feature.href} className="text-sm sm:text-base font-bold underline">Learn more</a>
                  </>
                }
              />
            );
          })}
        </div>

        {/* Bubble image */}
        <img
          src="/img/graphics/bubbles/bubble-top-orange.svg"
          alt="Bubble"
          className="absolute bottom-0 -right-0 lg:right-16 w-40 lg:w-60 z-10"
        />
        <img
          src="/img/graphics/bubbles/bubble-left-green.svg"
          alt="Bubble"
          className="absolute bottom-40 right-0 lg:right-12 w-28 lg:w-40 z-10"
        />
      </div>

      {/* Explore topics and collections */}
      <div className="grid grid-cols-1 gap-y-12 bg-dark-beige py-8 md:py-12 lg:py-16 text-center items-center justify-center">

        {/* Header */}
        <SectionHeader
          text="Explore topics and collections"
        />

        {/* Topics and collections */}
        <div className="flex flex-row flex-nowrap w-100vw overflow-auto gap-x-4 pl-4 pb-4">
          {exploreTopicsAndCollections.map((content, index) => {
            return (
              <CardWithImageBgAndBadgeTop
                objectId={content.id}
                objectType={content.type}
                showFavouriteButton={true}
                key={`explore-${index}`}
                colour={content.type === "Topic" ? "yellow" : "mint-green"}
                image={content.image === null ? "/img/graphics/hero/landing-hero.png" : content.image}
                href={content.type === "Topic" ? `/topic/${content.slug}` : `/collection/${content.slug}`}
                badgeText={content.type}
                cardText={content.name}
              />
            )
          })}
        </div>

      </div>

      {/* Pricing */}
      <div className="my-16 w-2/3 mx-auto">
        <PricingCard />
      </div>

      {/* Stats */}
      <HorizontalStats />

      {/* Get involved */}
      <div className="grid grid-cols-1 gap-y-12 bg-off-white py-8 md:py-12 lg:py-16 text-center items-center justify-center relative mb-12">

        {/* Header */}
        <div className="w-2/3 mx-auto grid grid-cols-1 lg:grid-cols-2 gap-y-6">
          <div className="grid grid-cols-1 gap-y-2 text-left">
            <h2 className="text-dark-grey text-xl md:text-2xl lg:text-3xl font-extrabold">Get involved</h2>
            <p className="text-dark-grey text-sm sm:text-base font-medium">
              We built RaiseDiversity for this community - help us shape it by sharing great content or adding your own.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="w-2/3 mx-auto grid grid-cols-1 lg:grid-cols-2">
          <div className="grid grid-cols-1 gap-y-10">
            <Card
              className="grid grid-cols-1 gap-y-4 relative overflow-visible z-10"
              colour="dark-beige"
              paddingX="px-8"
              paddingY="py-8"
            >
              <h2 className="text-dark-grey text-lg md:text-xl lg:text-2xl font-bold text-left">Suggest content you love</h2>
              <p className="text-dark-grey text-sm sm:text-base font-medium text-left">
                Have a favourite podcast, book or article? Share it with us to help others discover it too.
              </p>
              <Button
                onClick={() => window.location.href = "/contact"}
                width="fit"
                shadow="md"
              >
                <span>Suggest content</span>
              </Button>
            </Card>

            <Card
              className="grid grid-cols-1 gap-y-4 relative overflow-visible z-10"
              colour="dark-beige"
              paddingX="px-8"
              paddingY="py-8"
            >
              <h2 className="text-dark-grey text-lg md:text-xl lg:text-2xl font-bold text-left">Share your work with us</h2>
              <p className="text-dark-grey text-sm sm:text-base font-medium text-left">
                We’re building a space shaped by many voices. If you have something to add, we’d love to hear from you.
              </p>
              <Button
                onClick={() => window.location.href = "/contact"}
                width="fit"
                shadow="md"
                bgColour="mint-green"
                borderColour="mint-green"
              >
                <span>Discuss my content</span>
              </Button>
            </Card>
          </div>
        </div>

        {/* Images */}
        {/* Small top */}
        <img
          src="/img/graphics/images/Uni_student.jpg"
          className="hidden xl:block absolute h-44 w-52 object-cover top-28 right-96 rounded-6xl z-0"
        />
        {/* Medium left */}
        <img
          src="/img/graphics/images/Parents_bubbles_condensed.jpg"
          className="hidden lg:block absolute h-48 xl:h-60 w-48 xl:w-64 object-cover top-1/2 right-[25rem] xl:right-[35rem] rounded-6xl z-0"
        />
        {/* Large right */}
        <img
          src="/img/graphics/images/Homepage_women.jpg"
          className="hidden lg:block absolute h-72 w-80 xl:h-88 xl:w-96 object-cover top-60 right-10 rounded-6xl z-10"
        />

        {/* Bubbles */}
        <img
          src="/img/graphics/bubbles/bubble-right-green.svg"
          alt="Bubble"
          className="hidden xl:block absolute w-32 object-cover top-44 right-[33rem] z-0"
        />
        <img
          src="/img/graphics/bubbles/bubble-top-yellow.svg"
          alt="Bubble"
          className="absolute w-32 md:w-48 lg:w-64 xl:w-80 object-cover bottom-48 lg:bottom-32 xl:bottom-8 -right-4 md:right-10 lg:right-60 xl:right-72 z-10"
        />

      </div>

      {/* Support us */}
      <SupportUs />

    </Template>
  );
}
